import { useCallback, useMemo, useState } from 'react'

import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { SMB_CURRENCY_USD, SMB_FY_QUARTER, SMB_QUARTERS } from '../configs'
import {
  applySmbEditToStore,
  buildSmbRows,
  ensureQuarterSeed,
  ensureVerticalSeed,
  ensureVerticalSeedQuarters,
  findSmbNode,
  getPlanningSmbYears,
  getSmbAncestors,
  getSmbDefaultExpand,
  getSmbEffectiveValue,
  ISmbRow,
  isSameDirectSmbEdit,
  isSmbNodeExcluded,
  mapPlanningSmbToStore,
  mapStoreToPlanningSmbRows,
  normalizeSmbPercent,
  preservePlanningSmbUnshownYears,
  TGetSmbWeight,
  wouldSmbEditDiscardGenuineData,
} from '../lib'
import {
  IPlanningSmb,
  ISmbColumn,
  ISmbNode,
  TGetSmbFinancialValue,
  TSmbExpandedMap,
  TSmbSplitResult,
  TSmbSplitStore,
} from '../model/types'

const cloneStore = (store: TSmbSplitStore): TSmbSplitStore => {
  const next: TSmbSplitStore = {}

  Object.keys(store).forEach((key) => {
    const entry = store[key]
    if (entry) {
      next[key] = { ...entry }
    }
  })

  return next
}

const toFinancialValue = (value: number | null | undefined) =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0

export interface IPendingSmbEdit {
  nodeId: string
  nodeNameKey: string
  year: number
  fq: number
  smb: number
}

export interface IUsePlanningSmbEditor {
  tree: ISmbNode
  planningSmb: IPlanningSmb[]
  partnerId: string
  /** The current plan year can be quarterly; future Financial Plan years are annual. */
  fYear: number
  lastPlanYear?: number
  programType: string
  /** Metric and currency the Financial Plan itself is entered at - the baseline defaults to it */
  planActualsType: ActualsValueTypes
  /** Currency Planning is showing, the popup starts from it and then keeps its own */
  planCurrency: string
  /** Initial time grain copied from the Financial Planning category switcher. */
  initialIsQuarterly: boolean
  getFinancialValue: TGetSmbFinancialValue
}

export const usePlanningSmbEditor = ({
  tree,
  planningSmb,
  partnerId,
  fYear,
  lastPlanYear,
  programType,
  planActualsType,
  planCurrency,
  initialIsQuarterly,
  getFinancialValue,
}: IUsePlanningSmbEditor) => {
  const years = useMemo(() => getPlanningSmbYears(fYear, lastPlanYear), [fYear, lastPlanYear])

  /** Baseline stays anchored to the year before the plan, the same for every plan year */
  const baselineYear = fYear - 1

  const context = useMemo(
    () => ({ partnerId, programType, years }),
    [partnerId, programType, years]
  )
  const baselineContext = useMemo(
    () => ({ partnerId, programType, years: [baselineYear] }),
    [partnerId, programType, baselineYear]
  )

  const [initialStore] = useState<TSmbSplitStore>(() =>
    mapPlanningSmbToStore(planningSmb, context, tree)
  )
  const baselineStore = useMemo(
    () => mapPlanningSmbToStore(planningSmb, baselineContext, tree),
    [planningSmb, baselineContext, tree]
  )
  /** Financial Plan amount behind a cell - always at the plan own metric and currency */
  const getWeight = useCallback<TGetSmbWeight>(
    function resolveWeight(node: ISmbNode, year: number, fq: number): number {
      if (node.settingsIndex < 0) {
        return node.children
          .filter((child) => !isSmbNodeExcluded(child))
          .reduce((total, child) => total + resolveWeight(child, year, fq), 0)
      }

      return toFinancialValue(
        getFinancialValue({
          settingsIndex: node.settingsIndex,
          fq,
          type: 'targets',
          fYear: year,
          actualsType: planActualsType,
          isUSD: planCurrency === SMB_CURRENCY_USD,
          smbFraction: 'Total',
        })
      )
    },
    [getFinancialValue, planActualsType, planCurrency]
  )

  const hasPlanInSubtree = useCallback(
    function resolveHasPlan(node: ISmbNode): boolean {
      if (isSmbNodeExcluded(node)) {
        return false
      }

      const hasDirectPlan =
        node.settingsIndex >= 0 &&
        years.some((year) => {
          const quarters =
            year === fYear && initialIsQuarterly
              ? [...SMB_QUARTERS, SMB_FY_QUARTER]
              : [SMB_FY_QUARTER]

          return quarters.some((fq) => Math.abs(getWeight(node, year, fq)) > 0)
        })

      return hasDirectPlan || node.children.some(resolveHasPlan)
    },
    [fYear, getWeight, initialIsQuarterly, years]
  )

  const [store, setStore] = useState<TSmbSplitStore>(() => cloneStore(initialStore))
  const [isQuarterly, setIsQuarterly] = useState(initialIsQuarterly)
  // Stop the initial drill-down as soon as every nested child Plan is zero/empty. The branch
  // can still be opened manually; this only keeps empty detail out of the first view.
  const [initialExpanded] = useState<TSmbExpandedMap>(() =>
    getSmbDefaultExpand(tree, (node) => node.children.some(hasPlanInSubtree))
  )
  const [expanded, setExpanded] = useState<TSmbExpandedMap>(initialExpanded)
  const [baselineMetric, setBaselineMetric] = useState<ActualsValueTypes>(planActualsType)
  const [baselineCurrency, setBaselineCurrency] = useState(planCurrency)
  const [pendingEdit, setPendingEdit] = useState<IPendingSmbEdit | null>(null)

  /**
   * The baseline is the actuals of the year before the plan, read straight out of the
   * planning data at the selected metric and currency. It is never editable and never shifts
   * forward with the plan year.
   */
  const getDirectBaselineAmount = useCallback(
    (
      node: ISmbNode,
      fq: number,
      smbFraction: 'Total' | 'SMB' | 'EU',
      actualsType: ActualsValueTypes,
      isUSD: boolean
    ) =>
      node.settingsIndex < 0
        ? 0
        : toFinancialValue(
            getFinancialValue({
              settingsIndex: node.settingsIndex,
              fq,
              type: 'actuals',
              fYear: baselineYear,
              actualsType,
              isUSD,
              smbFraction,
            })
          ),
    [getFinancialValue, baselineYear]
  )

  const getBaselineWeight = useCallback(
    function resolveBaselineWeight(node: ISmbNode, fq: number): number {
      if (node.children.length) {
        return node.children
          .filter((child) => !isSmbNodeExcluded(child))
          .reduce((total, child) => total + resolveBaselineWeight(child, fq), 0)
      }

      return getDirectBaselineAmount(
        node,
        fq,
        'Total',
        baselineMetric,
        baselineCurrency === SMB_CURRENCY_USD
      )
    },
    [getDirectBaselineAmount, baselineMetric, baselineCurrency]
  )

  /** Classification roll-ups stay on the Financial Plan's fixed metric/currency. */
  const getBaselineClassificationWeight = useCallback(
    function resolveClassificationWeight(node: ISmbNode, fq: number): number {
      if (node.children.length) {
        return node.children
          .filter((child) => !isSmbNodeExcluded(child))
          .reduce((total, child) => total + resolveClassificationWeight(child, fq), 0)
      }

      return getDirectBaselineAmount(
        node,
        fq,
        'Total',
        planActualsType,
        planCurrency === SMB_CURRENCY_USD
      )
    },
    [getDirectBaselineAmount, planActualsType, planCurrency]
  )

  const rows = useMemo<ISmbRow[]>(() => buildSmbRows(tree, expanded), [tree, expanded])
  const expandedNodeIds = useMemo(
    () => new Set(rows.filter((row) => row.isExpanded).map((row) => row.node.id)),
    [rows]
  )
  /**
   * The previous-year classification is already persisted in PlanningSMB. Financial actuals
   * only weight hierarchy roll-ups and provide the optional money row; they do not recreate
   * the SMB/EU percentage.
   */
  const getBaselineValue = useCallback(
    (node: ISmbNode, fq: number): TSmbSplitResult =>
      getSmbEffectiveValue(
        baselineStore,
        node,
        baselineYear,
        fq,
        (weightNode, _year, weightFq) => getBaselineClassificationWeight(weightNode, weightFq),
        { ignoreDirectNodeIds: expandedNodeIds, allowPartialChildren: true }
      ),
    [baselineStore, baselineYear, getBaselineClassificationWeight, expandedNodeIds]
  )

  /**
   * Only the plan year is broken down by quarter; the two years after it are planned yearly
   * only, the same way the rest of the planning widget treats them.
   */
  const isQuarterlyYear = useCallback(
    (year: number) => year === fYear && isQuarterly,
    [fYear, isQuarterly]
  )

  const columns = useMemo<ISmbColumn[]>(
    () =>
      years.flatMap((year, index) => {
        const yearColumns: ISmbColumn[] =
          year === fYear && isQuarterly
            ? SMB_QUARTERS.map((fq) => ({ year, fq, isYearStart: false }))
            : []

        return [
          ...yearColumns.map((column, columnIndex) => ({
            ...column,
            isYearStart: index > 0 && columnIndex === 0,
          })),
          {
            year,
            fq: SMB_FY_QUARTER,
            isYearStart: index > 0 && yearColumns.length === 0,
          },
        ]
      }),
    [years, fYear, isQuarterly]
  )

  /**
   * Seeding runs on a working copy before anything is displayed: a row still showing an
   * editable field inherits from its nearest planned ancestor, and that value is then spread
   * across its quarters. An expanded row is always computed from what is below it, so there
   * is nothing to pre-fill on it. Each plan year seeds independently.
   */
  const preparedStore = useMemo(() => {
    const next = cloneStore(store)

    rows.forEach(({ node, ancestors, isExpanded }) => {
      if (isExpanded || isSmbNodeExcluded(node)) {
        return
      }

      years.forEach((year) => {
        const isQuarterlyGrain = year === fYear && isQuarterly

        if (ancestors.length) {
          if (isQuarterlyGrain) {
            ensureVerticalSeedQuarters(next, node, ancestors, year, getWeight)
          } else {
            ensureVerticalSeed(next, node, ancestors, year, SMB_FY_QUARTER, getWeight)
          }
        }

        if (isQuarterlyGrain) {
          ensureQuarterSeed(next, node.id, year)
        }
      })
    })

    return next
  }, [store, rows, years, fYear, isQuarterly, getWeight])

  const getCellValue = useCallback(
    (node: ISmbNode, year: number, fq: number): TSmbSplitResult =>
      getSmbEffectiveValue(preparedStore, node, year, fq, getWeight, {
        deriveFyFromQuarters: isQuarterlyYear(year) && fq === SMB_FY_QUARTER,
        ignoreDirectNodeIds: expandedNodeIds,
      }),
    [preparedStore, getWeight, isQuarterlyYear, expandedNodeIds]
  )

  const toggleExpanded = useCallback((nodeId: string) => {
    setExpanded((prev) => ({ ...prev, [nodeId]: !prev[nodeId] }))
  }, [])

  const applyEdit = useCallback(
    ({ nodeId, year, fq, smb }: IPendingSmbEdit) => {
      const node = findSmbNode(tree, nodeId)
      if (!node) {
        return
      }

      const ancestors = getSmbAncestors(tree, nodeId)

      setStore(() => {
        const next = cloneStore(preparedStore)

        applySmbEditToStore(next, {
          node,
          ancestors,
          year,
          fq,
          smb,
          getWeight,
          deriveFyFromQuarters: isQuarterlyYear(year),
        })

        return next
      })
    },
    [tree, preparedStore, getWeight, isQuarterlyYear]
  )

  /**
   * Commits one edited percentage. When the override would discard genuine, user entered
   * values below the edited row, the caller is handed a pending edit to confirm first.
   */
  const commitEdit = useCallback(
    (nodeId: string, year: number, fq: number, rawValue: number) => {
      const node = findSmbNode(tree, nodeId)
      if (!node) {
        return
      }

      const edit: IPendingSmbEdit = {
        nodeId,
        nodeNameKey: node.nameKey,
        year,
        fq,
        smb: normalizeSmbPercent(rawValue),
      }

      const ancestors = getSmbAncestors(tree, nodeId)
      if (isSameDirectSmbEdit(preparedStore, { ...edit, node, ancestors })) {
        return
      }

      const wouldDiscardGenuineData = wouldSmbEditDiscardGenuineData(preparedStore, node, year, fq)

      if (wouldDiscardGenuineData) {
        setPendingEdit(edit)

        return
      }

      applyEdit(edit)
    },
    [tree, preparedStore, applyEdit]
  )

  const confirmPendingEdit = useCallback(() => {
    if (pendingEdit) {
      applyEdit(pendingEdit)
    }
    setPendingEdit(null)
  }, [pendingEdit, applyEdit])

  const cancelPendingEdit = useCallback(() => setPendingEdit(null), [])

  /** Footer summary always talks about the plan year itself */
  const summary = useMemo(
    () =>
      getSmbEffectiveValue(preparedStore, tree, fYear, SMB_FY_QUARTER, getWeight, {
        deriveFyFromQuarters: isQuarterly,
        ignoreDirectNodeIds: expandedNodeIds,
      }),
    [preparedStore, tree, fYear, getWeight, isQuarterly, expandedNodeIds]
  )

  const initialRows = useMemo(
    () =>
      mapStoreToPlanningSmbRows(initialStore, tree, context, {
        getWeight,
        expanded: initialExpanded,
        derivePlanYearFyFromQuarters: initialIsQuarterly,
      }),
    [initialStore, tree, context, getWeight, initialExpanded, initialIsQuarterly]
  )
  const rowsToSave = useMemo(
    () =>
      mapStoreToPlanningSmbRows(store, tree, context, {
        getWeight,
        expanded,
        derivePlanYearFyFromQuarters: isQuarterly,
      }),
    [store, tree, context, getWeight, expanded, isQuarterly]
  )

  // Dirty state describes the final authoritative payload, not whether an input event ever
  // happened. Reverting to the loaded normalized picture therefore becomes clean again, while
  // changing hierarchy/time granularity remains a real change even when the visible % matches.
  const isDirty = useMemo(
    () => JSON.stringify(rowsToSave) !== JSON.stringify(initialRows),
    [rowsToSave, initialRows]
  )

  const getRowsToSave = useCallback(
    () =>
      preservePlanningSmbUnshownYears(rowsToSave, planningSmb, {
        partnerId,
        programType,
        fYear,
        lastPlanYear,
      }),
    [rowsToSave, planningSmb, partnerId, programType, fYear, lastPlanYear]
  )

  return {
    rows,
    columns,
    years,
    isQuarterly,
    setIsQuarterly,
    isQuarterlyYear,
    expanded,
    toggleExpanded,
    baselineMetric,
    setBaselineMetric,
    baselineCurrency,
    setBaselineCurrency,
    getWeight,
    getBaselineWeight,
    getCellValue,
    getBaselineValue,
    commitEdit,
    pendingEdit,
    confirmPendingEdit,
    cancelPendingEdit,
    summary,
    isDirty,
    getRowsToSave,
  }
}
